import { useCallback, useMemo, useState } from "react";
import { GymCreateEditor, GymEditor } from "../gyms/gym-editors";
import { ExportCsvEditor, ImportEditor } from "../history/history-editors";
import { DeleteEditor, ExportEditor, NameEditor } from "./account-editors";
import { MeasurementEditor } from "./body-editors";
import {
  defaultDescribeError,
  type EditorHost,
  type SavedDetail,
} from "./editor-shell";
import { EquipmentEditor, PlaceEditor } from "./equipment-editors";
import { PainEditor, ParqEditor } from "./health-editors";
import {
  type EditorId,
  gymIdOf,
  painIndexOf,
  type StaticEditorId,
} from "./ids";
import { ReminderEditor } from "./reminder-editor";
import {
  ExperienceEditor,
  GoalEditor,
  RoutineEditor,
  SecondaryEditor,
  SessionMinutesEditor,
  SideEditor,
} from "./training-editors";
import {
  BirthEditor,
  HeightEditor,
  ModalitiesEditor,
  MotivationEditor,
  ProfileChoiceEditor,
  SleepEditor,
} from "./you-editors";

export interface EditorSheetProps {
  /**
   * The failed-write sentence. T51 passes the app's `useErrorMessage()`;
   * without it the editors render the one refusal they expect by key and a
   * generic line for the rest (`defaultDescribeError`).
   */
  describeError?: (error: unknown) => string;
  /** The open editor — `?edit=` — or `null` when none is. */
  edit: EditorId | null;
  /** `false` while the coach is docked on desktop (D04). */
  modal?: boolean;
  /** Bind to the router's `closeLayer()`; Escape, ✕, Cancel and a swipe. */
  onClose: () => void;
  /** A save landed: flash that row (`p-saved`, T51) and show `detail`. */
  onSaved?: (edit: EditorId, detail: SavedDetail) => void;
}

/**
 * The dispatcher: an edit id to its editor, each wrapped in T14's `Sheet`
 * with A2's header-only drag (`EditSheet`, A2 profile/controls.tsx:26-77).
 *
 * It knows nothing of the router (T51 binds `edit` to `?edit=` and `onClose`
 * to `closeLayer`), and nothing of the row that opened it — `onSaved` is how
 * the profile learns a row changed (A2's `MARK_SAVED` event, as a callback).
 *
 * The last editor stays mounted, closed, after `edit` goes back to `null`,
 * so the sheet animates out instead of vanishing; a new id remounts it
 * (`key`), so every opening starts from the server value.
 */
export function EditorSheet({
  edit,
  onClose,
  onSaved,
  describeError,
  modal,
}: EditorSheetProps) {
  const [shown, setShown] = useState<EditorId | null>(edit);
  if (edit !== null && edit !== shown) {
    setShown(edit);
  }

  const saved = useCallback(
    (detail: SavedDetail) => {
      if (shown !== null) {
        onSaved?.(shown, detail);
      }
    },
    [onSaved, shown]
  );
  const host = useMemo<EditorHost>(
    () => ({
      describeError: describeError ?? defaultDescribeError,
      modal,
      onClose,
      onSaved: saved,
      open: edit !== null,
    }),
    [describeError, modal, onClose, saved, edit]
  );

  if (shown === null) {
    return null;
  }
  return <Editor host={host} id={shown} key={shown} />;
}

function Editor({ id, host }: { host: EditorHost; id: EditorId }) {
  const painIndex = painIndexOf(id);
  if (painIndex !== null) {
    return <PainEditor host={host} index={painIndex} />;
  }
  const gymId = gymIdOf(id);
  if (gymId !== null) {
    return <GymEditor gymId={gymId} host={host} />;
  }
  switch (id as StaticEditorId) {
    case "name":
      return <NameEditor host={host} />;
    case "birth":
      return <BirthEditor host={host} />;
    case "sex":
      return <ProfileChoiceEditor field="sex" host={host} />;
    case "height":
      return <HeightEditor host={host} />;
    case "side":
      return <SideEditor host={host} />;
    case "motivation":
      return <MotivationEditor host={host} />;
    case "weight":
      return <MeasurementEditor host={host} kind="weight" />;
    case "bodyFat":
      return <MeasurementEditor host={host} kind="bodyFat" />;
    case "goal":
      return <GoalEditor host={host} />;
    case "secondary":
      return <SecondaryEditor host={host} />;
    case "experience":
    case "stoppedAgo":
    case "trainedFor":
      return <ExperienceEditor host={host} />;
    case "same":
      return <RoutineEditor host={host} startAt="same" />;
    case "days":
      return <RoutineEditor host={host} startAt="days" />;
    case "sessionMinutes":
      return <SessionMinutesEditor host={host} />;
    case "place":
      return <PlaceEditor host={host} />;
    case "equipment":
      return <EquipmentEditor host={host} />;
    case "occupation":
      return <ProfileChoiceEditor field="occupation" host={host} />;
    case "sleep":
      return <SleepEditor host={host} />;
    case "protein":
      return <ProfileChoiceEditor field="proteinMeals" host={host} />;
    case "supplement":
      return <ProfileChoiceEditor field="proteinSupplement" host={host} />;
    case "modalities":
      return <ModalitiesEditor host={host} />;
    case "parq":
      return <ParqEditor host={host} />;
    case "reminder":
      return <ReminderEditor host={host} />;
    case "pain-new":
      return <PainEditor host={host} index={null} />;
    case "export":
      return <ExportEditor host={host} />;
    case "export-csv":
      return <ExportCsvEditor host={host} />;
    case "import":
      return <ImportEditor host={host} />;
    case "gym-new":
      return <GymCreateEditor host={host} />;
    case "delete":
      return <DeleteEditor host={host} />;
    default:
      return null;
  }
}
